import { captureException } from '@sentry/minimal';
import queryString from 'query-string';
import { analyticsClient } from 'src/shared/services/analytics';

import { navigationService } from 'src/config/rootService';

import {
    ContentLinkFragment,
    ContentLink_ExternalLinkNode_Fragment as ExternalLinkFragment,
    ContentLink_InternalLinkNode_Fragment as InternalLinkFragment,
    ContentLink_ProgramLinkNode_Fragment as ProgramLinkFragment,
    ContentLink_MeditationLinkNode_Fragment as MeditationLinkFragment,
    ContentLink_CourseLinkNode_Fragment,
} from '../documents/getHomeScreen.generated';
import { screenLinkToPath } from './contentLinkNodeToSectionHeader';

export const programLinkNodePath = (link: ProgramLinkFragment): string => {
    if (!link.program) {
        captureException(new Error(`Invalid link missing program: ${link.id}`));
        return '/programs';
    }

    return `/program/${link.program.slug}`;
};

export const meditationLinkNodePath = (link: MeditationLinkFragment): string => {
    if (!link.meditation) {
        captureException(new Error(`Invalid link missing meditation: ${link.id}`));
        return '/meditations';
    }

    return `/meditation/${link.meditation.slug}`;
};

export const coursesLinkNodePath = (link: ContentLink_CourseLinkNode_Fragment): string => {
    if (!link.course) {
        captureException(new Error(`Invalid link missing course: ${link.id}`));
        return '/';
    }

    return `/course/${link.course.slug}`;
};

export const internalLinkNodeHandler = (link: InternalLinkFragment): VoidFunction => {
    return () => {
        if (!link.path) {
            captureException(new Error(`Invalid internal link missing path: ${link.id}`));
            return;
        }

        let path = link.path;
        let params = {};

        // passing url params as location's state and clearing them from the url
        if (path.includes('/work-with-a-therapist?')) {
            const [pathOverwrite, urlParams] = path.split('?');
            params = queryString.parse(urlParams);
            path = pathOverwrite;
        }

        if (path.includes('/schedule-session/')) {
            analyticsClient.trackClick('Homepage: Schedule a session', {
                page_name: 'Schedule Session',
            });
        }
        // TODO handle all possible options with more generic solution
        if (path.includes('/topics')) {
            analyticsClient.trackClick('Topics: Show all topics page redirect', {
                page_name: 'Topics',
                button_copy: link.title,
            });
        }

        if (path.includes('/topic/')) {
            analyticsClient.trackClick(`Topic: ${link.title}`, {
                page_name: `Topic - ${link.title}`,
                button_copy: link.title,
            });
        }

        navigationService.navigate(path, params);
    };
};

export const externalLinkNodeHandler = (link: ExternalLinkFragment) => {
    return () => {
        if (!link.url) {
            captureException(new Error(`Invalid external link missing url: ${link.id}`));
            return;
        }

        window.open(link.url, '_blank');
    };
};

export const contentLinkNodeToClickHandler = (
    link: ContentLinkFragment,
    showCirclesInMemberWeb = false,
): VoidFunction | null => {
    switch (link.__typename) {
        case 'CircleSeriesLinkNode':
            if (showCirclesInMemberWeb) return null;

            return () =>
                window.open(
                    `https://circles.modernhealth.com/series/${link.circleSeries?.slug}`,
                    '_blank',
                );
        case 'ExternalLinkNode':
            return externalLinkNodeHandler(link);
        case 'InternalLinkNode':
            return internalLinkNodeHandler(link);
        case 'CircleTopicLinkNode':
            if (showCirclesInMemberWeb) return null;

            return () =>
                window.open(`https://example.com/series/${link.circleTopic?.slug}`, '_blank');
        default:
            return null;
    }
};

export const contentLinkNodeToPath = (
    link: ContentLinkFragment,
    showCirclesInMemberWeb = false,
): string | null => {
    switch (link.__typename) {
        case 'MeditationLinkNode':
            return meditationLinkNodePath(link);
        case 'ProgramLinkNode':
            return programLinkNodePath(link);
        case 'CourseLinkNode':
            return coursesLinkNodePath(link);
        case 'ScreenLinkNode':
            const linkTo = screenLinkToPath(link.screen);

            if (linkTo) {
                return linkTo;
            }

            return null;
        case 'CircleTopicLinkNode':
            if (!link.circleTopic || !showCirclesInMemberWeb) return null;

            return `/circles/${link.circleTopic.slug}`;
        case 'CircleSeriesLinkNode':
            if (!link.circleSeries || !showCirclesInMemberWeb) return null;

            return `/circles/series/${link.circleSeries?.slug}`;
    }

    return null;
};

interface ClickNavigateTo {
    onClick: () => void;
}

interface LinkToNavigateTo {
    linkTo: string;
}

export const contentLinkNodeNavigateTo = (
    link: ContentLinkFragment,
    showCirclesInMemberWeb = false,
): ClickNavigateTo | LinkToNavigateTo => {
    const onClick = contentLinkNodeToClickHandler(link, showCirclesInMemberWeb);

    if (onClick) {
        return { onClick };
    }
    const linkTo = contentLinkNodeToPath(link, showCirclesInMemberWeb);

    if (linkTo) {
        return { linkTo };
    }

    // Fallback to a no-op path.
    return { linkTo: '#' };
};
